import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { DisposableStore } from 'vscode/vscode/vs/base/common/lifecycle';
import { getWindow, addDisposableListener, EventType } from 'vscode/vscode/vs/base/browser/dom';
import { RunOnceScheduler } from 'vscode/vscode/vs/base/common/async';
import { convertBufferRangeToViewport } from './terminalLinkHelpers.js';
import { isMacintosh } from 'vscode/vscode/vs/base/common/platform';
import { Emitter } from 'vscode/vscode/vs/base/common/event';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';

let TerminalLink = class TerminalLink extends DisposableStore {
    get onInvalidated() { return this._onInvalidated.event; }
    get type() { return this._type; }
    constructor(_xterm, range, text, uri, parsedLink, actions, _viewportY, _activateCallback, _tooltipCallback, _isHighConfidenceLink, label, _type, _configurationService) {
        super();
        this._xterm = _xterm;
        this.range = range;
        this.text = text;
        this.uri = uri;
        this.parsedLink = parsedLink;
        this.actions = actions;
        this._viewportY = _viewportY;
        this._activateCallback = _activateCallback;
        this._tooltipCallback = _tooltipCallback;
        this._isHighConfidenceLink = _isHighConfidenceLink;
        this.label = label;
        this._type = _type;
        this._configurationService = _configurationService;
        this._onInvalidated = ( new Emitter());
        this.decorations = {
            pointerCursor: false,
            underline: this._isHighConfidenceLink
        };
    }
    dispose() {
        super.dispose();
        this._hoverListeners?.dispose();
        this._hoverListeners = undefined;
        this._tooltipScheduler?.dispose();
        this._tooltipScheduler = undefined;
    }
    activate(event, text) {
        this.asyncActivate = this._activateCallback(event, text);
    }
    hover(event, text) {
        const w = getWindow(event);
        const d = w.document;
        this._hoverListeners = ( new DisposableStore());
        this._hoverListeners.add(addDisposableListener(d, 'keydown', e => {
            if (!e.repeat && this._isModifierDown(e)) {
                this._enableDecorations();
            }
        }));
        this._hoverListeners.add(addDisposableListener(d, 'keyup', e => {
            if (!e.repeat && !this._isModifierDown(e)) {
                this._disableDecorations();
            }
        }));
        this._hoverListeners.add(this._xterm.onRender(e => {
            const viewportRangeY = this.range.start.y - this._viewportY;
            if (viewportRangeY >= e.start && viewportRangeY <= e.end) {
                this._onInvalidated.fire();
            }
        }));
        if (this._isHighConfidenceLink) {
            this._tooltipScheduler = ( new RunOnceScheduler(() => {
                this._tooltipCallback(this, convertBufferRangeToViewport(this.range, this._viewportY), this._isHighConfidenceLink ? () => this._enableDecorations() : undefined, this._isHighConfidenceLink ? () => this._disableDecorations() : undefined);
                this._tooltipScheduler?.dispose();
                this._tooltipScheduler = undefined;
            }, this._configurationService.getValue('workbench.hover.delay')));
            this.add(this._tooltipScheduler);
            this._tooltipScheduler.schedule();
        }
        const origin = { x: event.pageX, y: event.pageY };
        this._hoverListeners.add(addDisposableListener(d, EventType.MOUSE_MOVE, e => {
            if (this._isModifierDown(e)) {
                this._enableDecorations();
            }
            else {
                this._disableDecorations();
            }
            if (Math.abs(e.pageX - origin.x) > w.devicePixelRatio * 2 || Math.abs(e.pageY - origin.y) > w.devicePixelRatio * 2) {
                origin.x = e.pageX;
                origin.y = e.pageY;
                this._tooltipScheduler?.schedule();
            }
        }));
    }
    leave() {
        this._hoverListeners?.dispose();
        this._hoverListeners = undefined;
        this._tooltipScheduler?.dispose();
        this._tooltipScheduler = undefined;
    }
    _enableDecorations() {
        if (!this.decorations.pointerCursor) {
            this.decorations.pointerCursor = true;
        }
        if (!this.decorations.underline) {
            this.decorations.underline = true;
        }
    }
    _disableDecorations() {
        if (this.decorations.pointerCursor) {
            this.decorations.pointerCursor = false;
        }
        if (this.decorations.underline !== this._isHighConfidenceLink) {
            this.decorations.underline = this._isHighConfidenceLink;
        }
    }
    _isModifierDown(event) {
        const multiCursorModifier = this._configurationService.getValue('editor.multiCursorModifier');
        if (multiCursorModifier === 'ctrlCmd') {
            return !!event.altKey;
        }
        return isMacintosh ? event.metaKey : event.ctrlKey;
    }
};
TerminalLink = ( __decorate([
    ( __param(12, IConfigurationService))
], TerminalLink));

export { TerminalLink };
